import { FC, useState } from 'react'
import { useLocation } from 'react-router-dom'

import { ResourceHeader } from '@factorialco/f0-react/dist/experimental'
import { CompetenciesCompetencyCrumbFragment } from 'generated/resources/graphql'
import { compact } from 'lodash'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'

import CompetencySettingsHeader from 'modules/competencies/components/CompetencySettingsHeader'
import { EditCompetencyModal } from 'modules/competencies/components/EditCompetencyModal'
import { type CompetenciesConfig } from 'modules/competencies/hooks/useCompetenciesFeatures'

const CompetencyHeader: FC<{
  competency: CompetenciesCompetencyCrumbFragment
  assignedJobsCount: number
  featuresConfig: CompetenciesConfig
  canManageCompetencies: boolean
  canSeeJobCatalog: boolean
}> = ({
  competency,
  assignedJobsCount,
  featuresConfig,
  canManageCompetencies,
  canSeeJobCatalog,
}) => {
  const { navigation } = useNavigation()
  const location = useLocation()
  const settingsPath = navigation.competencies.competencyId.settings.fullPath({
    competencyId: Number(competency.id),
  })

  const { isJobCatalogTreeEnabled } = featuresConfig
  const [showEditCompetencyModal, setShowEditCompetencyModal] = useState(false)

  // TODO: remove this when the job catalog tree feature is enabled in production for everyone
  // this is a temporary solution to show the correct competency header for settings page
  // without using the resource: true for the settings page
  // as this will have a visual bug with the redirect as the breadcrumbs do not mantain the state or history
  if (!isJobCatalogTreeEnabled || location.pathname === settingsPath) {
    return (
      <CompetencySettingsHeader
        competencyId={Number(competency.id)}
        competencyName={competency?.name || i18n.t('competencies_v3.header.untitled')}
      />
    )
  }

  return (
    <>
      <ResourceHeader
        title={competency?.name || i18n.t('competencies_v3.header.untitled')}
        description={competency?.description || i18n.t('competencies_v3.header.no_description')}
        secondaryActions={
          canManageCompetencies
            ? [
                {
                  label: i18n.t('competencies_v3.header.edit'),
                  onClick: () => setShowEditCompetencyModal(true),
                },
              ]
            : []
        }
        metadata={compact([
          {
            label: i18n.t('competencies_v3.header.group'),
            value: {
              type: 'text' as const,
              content: competency?.competencyGroup?.name || '-',
            },
          },
          {
            label: i18n.t('competencies_v3.levels.title'),
            value: {
              type: 'text' as const,
              content: String(
                competency?.competencyLevelsConnection?.totalCount ||
                  i18n.t('competencies_v3.header.no_levels')
              ),
            },
          },
          canSeeJobCatalog && {
            label: i18n.t('competencies_v3.header.assigned_jobs'),
            value: {
              type: 'text' as const,
              content: String(assignedJobsCount || i18n.t('competencies_v3.header.no_jobs')),
            },
          },
        ])}
      />
      {showEditCompetencyModal && (
        <EditCompetencyModal
          competency={competency}
          onClose={() => setShowEditCompetencyModal(false)}
        />
      )}
    </>
  )
}

export default CompetencyHeader
